import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import confirm from 'modules/confirm';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { Button, Loader } from 'ncoded-component-library';
import {
  getArrayLength,
  getHasElementsInArray,
  getShowGroupChat,
  getUserFullName,
  isEmail,
  normalizeEmail,
} from 'utils';
import { getMainGroupInfoPayloadValues } from '../AddGroupMember/components/BulkAddMember/utils';
import TrashIcon from 'icons/Trash.icon';
import NoEntryIcon from 'icons/NoEntry.icon';
import ChatIcon from 'icons/Chat.icon';
import SettingsIcon from 'icons/Settings.icon';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import UserMinusIcon from 'icons/UserMinus.icon';
import KeyActions from 'router/subrouters/DataRoom/components/KeyActions';
import SegmentedToggle from 'components/SegmentedToggle';
import MessagingProvider from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/providers/Messaging';
import Chat from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/pages/Chat';
import { MESSAGE_LOCATION } from 'models/Message';
import {
  DataRoomBuySideGroup,
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRevokeFileAccess from '../../hooks/useRevokeFIleAccess';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { MainGroupInfoForm } from '../AddGroupMember/components/BulkAddMember/BulkAddMember.component';
import useDeleteMember from 'router/subrouters/DataRoom/hooks/useDeleteMember';
import useConfirmRevokeInOrder from 'router/subrouters/DataRoom/hooks/useConfirmRevokeInOrder';

import './ManageBuyerGroup.styles.scss';

type TabValue = 'settings' | 'chat';

type ManageBuyerGroupProps = {
  className?: string;
  group: DataRoomBuySideGroup;
};

const ManageBuyerGroup: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageBuyerGroupProps
> = (props, ref) => {
  const { className, group } = props;

  const classes = classNames('manage-buyer-group', className);

  const { name, activeChatSessionId } = group || {};

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue>('settings');
  const [members, setMembers] = useState<DataRoomMember[]>(
    group?.dataRoomMembers ?? [],
  );
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { doesntHaveAccess, dataRoom } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { updateBuySideGroup, setBuySideGroups, setSellSideMembers } =
    useContext(MembersContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const isChatOpened = useMemo(() => activeTab === 'chat', [activeTab]);
  const isSettingsOpened = useMemo(() => activeTab === 'settings', [activeTab]);

  // Keep the local members list in sync when a different group is opened.
  useEffect(() => {
    setMembers(group?.dataRoomMembers ?? []);
    setActiveTab('settings');
  }, [group]);

  const canGroupChat = useMemo(
    () => getShowGroupChat(activeChatSessionId, members),
    [activeChatSessionId, members],
  );

  // Fall back to settings if the chat becomes unavailable (e.g. members drop below two).
  useEffect(() => {
    if (!canGroupChat && isChatOpened) setActiveTab('settings');
  }, [canGroupChat, isChatOpened]);

  const initialValues = useMemo(
    () =>
      ({
        name,
        buySideGroupMembers: [],
      } as const),
    [name],
  );

  const numberOfMembersWithAccess = useMemo(
    () =>
      getArrayLength(
        members.filter(({ accessableDataRoomNodesView }) =>
          getHasElementsInArray(accessableDataRoomNodesView),
        ),
      ),
    [members],
  );

  const handleOnRevokeAccess = useCallback(
    () => dataRoomApi.members.revokeAccessGroup(group._id),
    [group],
  );

  const handleOnUndoRevokeAccess = useCallback(
    () => dataRoomApi.members.undoRevokeAccessGroup(group._id),
    [group],
  );

  const { confirmRevokeAll } = useConfirmRevokeInOrder();

  const confirmRevokeAccess = useCallback(
    () =>
      confirmRevokeAll(
        members.filter(({ isLead }) => isLead).map(({ _id }) => _id),
      ),
    [confirmRevokeAll, members],
  );

  const updateValueOnRevokeSuccess = useCallback(
    (group: DataRoomBuySideGroup) =>
      updateBuySideGroup({
        ...group,
        dataRoomMembers: group.dataRoomMembers.map((member) => ({
          ...member,
          accessableDataRoomNodesView: [],
        })),
      }),
    [updateBuySideGroup],
  );

  const {
    isLoadingRevoke,
    isUndoAvailable,
    hasFileAccess,
    onRevokeAccess,
    onUndoRevokeAccess,
  } = useRevokeFileAccess({
    value: group,
    numberOfAccess: numberOfMembersWithAccess,
    handleOnRevokeAccess,
    handleOnUndoRevokeAccess,
    confirmRevokeAccess,
    updateValueOnRevokeSuccess,
    updateValueOnUndoSuccess: updateBuySideGroup,
  });

  const updateMembers = useCallback(
    (data: DataRoomMembers) => {
      const { sellSideMembers, buySideGroups } = data;
      setSellSideMembers(sellSideMembers);
      setBuySideGroups(buySideGroups);
      drawer?.setOpen(false);
    },
    [drawer, setBuySideGroups, setSellSideMembers],
  );

  const deleteMember = useDeleteMember();

  const onRemoveMember = useCallback(
    async (member: DataRoomMember) => {
      deleteMember({
        member,
        groupName: name,
        setIsLoading,
        onSuccess: (data) => {
          setSellSideMembers(data.sellSideMembers);
          setBuySideGroups(data.buySideGroups);

          const updatedGroup = data.buySideGroups.find(
            ({ _id }) => _id === group?._id,
          );

          if (updatedGroup) setMembers(updatedGroup.dataRoomMembers);
          else drawer?.setOpen(false);
        },
      });
    },
    [
      deleteMember,
      drawer,
      group?._id,
      name,
      setBuySideGroups,
      setSellSideMembers,
    ],
  );

  const onDeleteGroup = useCallback(async () => {
    if (
      await confirm({
        title: t('deleteGroup'),
        content: t('deleteGroupMessage', {
          groupName: name,
          dataRoomName: dataRoom?.name,
        }),
        submitBtnText: t('deleteGroup'),
      })
    ) {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.deleteGroup(group._id);

        updateMembers(data);
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [group, name, dataRoom?.name, updateMembers, popServerError, t]);

  const onSubmit = useCallback(
    async (values: MainGroupInfoForm) => {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.updateBuySideGroup(
          group._id,
          getMainGroupInfoPayloadValues(values),
        );

        updateMembers(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [group, popServerError, updateMembers],
  );

  if (doesntHaveAccess) return null;

  return (
    <>
      <SideDrawer
        ref={drawerRef}
        title={
          <>
            {name}

            <span className="manage-buyer-group__count">
              <GroupUsersIcon />
              {getArrayLength(members)}
            </span>
          </>
        }
        className={classes}
      >
        <div className="manage-buyer-group__header">
          <SegmentedToggle
            className={classNames({ 'has-disabled': !canGroupChat })}
            selectedValue={activeTab}
            onChange={(value) => setActiveTab(value as TabValue)}
            options={[
              {
                value: 'chat',
                disabled: !canGroupChat,
                tooltip: !canGroupChat && t('groupChatUnavailable'),
                label: (
                  <>
                    <ChatIcon />
                    {t('groupChat')}
                  </>
                ),
              },
              {
                value: 'settings',
                label: (
                  <>
                    <SettingsIcon />
                    {t('groupSettings')}
                  </>
                ),
              },
            ]}
          />
        </div>

        {drawer?.open && isChatOpened && canGroupChat && (
          <div className="manage-buyer-group__chat">
            <MessagingProvider sessionId={activeChatSessionId}>
              <Chat messageLocation={MESSAGE_LOCATION.DATA_ROOM} />
            </MessagingProvider>
          </div>
        )}

        {drawer?.open && isSettingsOpened && (
          <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({
              invalid,
              values: { buySideGroupMembersUnsubmited },
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="manage-buyer-group__body">
                  <Field
                    name="name"
                    label={t('groupName')}
                    description={t('helpColleaguesUnderstandRepresents')}
                    component={InputField}
                    validate={validations.required()}
                    required
                  />

                  <div className="members">
                    <p>{t('members')}</p>

                    {members.map((member) => {
                      const { _id, firstName, lastName, email } = member;

                      return (
                        <div key={_id}>
                          <div className="member-info">
                            <p>
                              {getUserFullName(firstName, lastName) ||
                                t('invited')}
                            </p>
                            {email && <p>{normalizeEmail(email)}</p>}
                          </div>

                          <Button
                            type="button"
                            variant="icon"
                            icon={UserMinusIcon}
                            className="remove-member-button"
                            onClick={() => onRemoveMember(member)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <Field
                    name="buySideGroupMembers"
                    label={t('inviteMoreBuyMembers')}
                    component={TextareaEmailsField}
                    description={`${t('buySideGroupNote')} ${t(
                      'weAreGoingToSendInvitationsViaEmail',
                    )}`}
                  />

                  <KeyActions description={t('keyActionsGroupDescription')}>
                    <>
                      {hasFileAccess && (
                        <Button
                          onClick={onRevokeAccess}
                          icon={NoEntryIcon}
                          disabled={isLoadingRevoke}
                          variant="outline"
                        >
                          {t('revokeAccessForAll')}
                        </Button>
                      )}

                      <Button
                        onClick={onDeleteGroup}
                        icon={TrashIcon}
                        variant="solid"
                      >
                        {t('deleteThisGroup')}
                      </Button>
                    </>
                  </KeyActions>
                </div>

                <div className="manage-buyer-group__footer">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => drawer?.setOpen(false)}
                  >
                    {t('cancel')}
                  </Button>

                  <Button
                    type="submit"
                    variant="solid"
                    disabled={
                      invalid ||
                      (buySideGroupMembersUnsubmited &&
                        !isEmail(buySideGroupMembersUnsubmited))
                    }
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            )}
          />
        )}

        {isLoading && (
          <div className="manage-buyer-group__loader">
            <Loader />
          </div>
        )}
      </SideDrawer>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingRevoke}
        onUndo={onUndoRevokeAccess}
      >
        <p>{t('fileAccessRevokedGroup')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(ManageBuyerGroup);
